import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion, Reorder } from 'framer-motion'
import { useAnagram } from '../hooks/useAnagram'
import { Alert } from '../components/Alert'
import { WordLength } from '../types/game'
import { isValidMode } from '../utils/validation'
import { hasAnagramModeBeenPlayed } from '../utils/storage'

const AnagramGamePage = () => {
  const { mode } = useParams<{ mode: string }>()
  const navigate = useNavigate()
  const modeValue = mode || '5'
  const wordLength = Number(modeValue) as WordLength

  useEffect(() => {
    if (!mode || !isValidMode(mode)) {
      navigate('/anagram')
      return
    }
    if (hasAnagramModeBeenPlayed(wordLength)) {
      navigate('/anagram')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const {
    tiles,
    setTiles,
    attemptCount,
    submitAttempt,
    gameOver,
    won,
    secondsTaken,
    targetWord,
    definition,
    setterNote,
    isLoading,
    alerts,
  } = useAnagram({ mode: wordLength })

  useEffect(() => {
    if (gameOver) {
      navigate('/anagram/complete', {
        state: {
          won,
          targetWord,
          definition,
          setterNote,
          attemptCount,
          secondsTaken,
          mode: wordLength,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOver])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitAttempt()
  }

  if (!mode || !isValidMode(mode)) return null

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="game-header">
        <h1 className="game-title">Anagram: {modeValue} Letter</h1>
      </div>

      <div className="container">
        <div className="game-container">
          <Alert alerts={alerts} />

          {isLoading && tiles.length === 0 ? (
            <div className="loading-spinner">Loading...</div>
          ) : (
            <>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col items-center gap-4"
              >
                <Reorder.Group
                  as="div"
                  axis="x"
                  values={tiles}
                  onReorder={setTiles}
                  role="grid"
                  aria-label="Drag the letters to reorder your guess"
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 'var(--tile-gap)',
                    margin: '2rem 0',
                    padding: 0,
                    listStyle: 'none',
                  }}
                >
                  {tiles.map((tile) => (
                    <Reorder.Item
                      as="div"
                      key={tile.id}
                      value={tile}
                      role="cell"
                      className="grid-cell"
                      whileDrag={{ scale: 1.08 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 'var(--tile-size)',
                        height: 'var(--tile-size)',
                        fontWeight: 'bold',
                        fontSize: 'var(--tile-font-size)',
                        textTransform: 'uppercase',
                        border: '2px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '4px',
                        background: 'rgba(0, 0, 0, 0.3)',
                        cursor: isLoading ? 'default' : 'grab',
                      }}
                      drag={!isLoading}
                    >
                      {tile.letter.toUpperCase()}
                    </Reorder.Item>
                  ))}
                </Reorder.Group>

                <button
                  type="submit"
                  className="play-button"
                  disabled={isLoading}
                >
                  Send Guess
                </button>
              </form>

              {attemptCount > 0 && (
                <p className="text-center text-sm text-white/50 mt-4">
                  {attemptCount} attempt{attemptCount === 1 ? '' : 's'} so far
                  &mdash; unlimited tries.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default AnagramGamePage
